<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard headline-id="documents" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('documents')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('documents')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('documents')"
                    @enter-edit-mode="onEnterEditMode('documents', $t('series-annual-documents'))"
                    @leave-edit-mode="leaveEditMode('documents')"
                    @discard-changes="discardChanges('documents')"
                    @save="saveForm('documents')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-documents-help')">
                        {{ $t('series-annual-documents') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesDocumentsForm
                        v-if="showForm('documents')"
                        ref="eventSeriesDocumentsFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="extraDocuments.length"
                            :label="$t('extra-documents')"
                            :items="extraDocuments"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('extra-documents')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="systems" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('systems')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('systems')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('systems')"
                    @enter-edit-mode="onEnterEditMode('systems', $t('series-annual-systems'))"
                    @leave-edit-mode="leaveEditMode('systems')"
                    @discard-changes="discardChanges('systems')"
                    @save="saveForm('systems')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-systems-help')">
                        {{ $t('series-annual-systems') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesSystemsForm
                        v-if="showForm('systems')"
                        ref="eventSeriesSystemsFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="extraSystems.length"
                            :label="$t('extra-systems')"
                            :items="extraSystems"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('extra-systems')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="data" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('data')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('data')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('data')"
                    @enter-edit-mode="onEnterEditMode('data', $t('series-annual-data'))"
                    @leave-edit-mode="leaveEditMode('data')"
                    @discard-changes="discardChanges('data')"
                    @save="saveForm('data')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-data-help')">
                        {{ $t('series-annual-data') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesDataForm
                        v-if="showForm('data')"
                        ref="eventSeriesDataFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="externalData.length"
                            :label="$t('external-data')"
                            :items="externalData"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('external-data')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="internalData.length"
                            :label="$t('internal-data')"
                            :items="internalData"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('internal-data')" text="-" :has-border-bottom="false" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="subevents" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('subevents')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('subevents')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('subevents')"
                    @enter-edit-mode="onEnterEditMode('subevents', $t('series-subevents'))"
                    @leave-edit-mode="leaveEditMode('subevents')"
                    @discard-changes="discardChanges('subevents')"
                    @save="saveForm('subevents')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('series-subevents-help')">
                        {{ $t('series-subevents') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesSubEventsForm
                        v-if="showForm('subevents')"
                        ref="eventSeriesSubEventsForm"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="subeventsLocal.length"
                            :label="$t('subevents-local')"
                            :items="subeventsLocal"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('subevents-local')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="subeventsOtherLocations.length"
                            :label="$t('subevents-other-locations')"
                            :items="subeventsOtherLocations"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('subevents-other-locations')"
                            text="-"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="fair" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('fair')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('fair')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('fair')"
                    @enter-edit-mode="onEnterEditMode('fair', $t('series-annual-fair-abroad'))"
                    @leave-edit-mode="leaveEditMode('fair')"
                    @discard-changes="discardChanges('fair')"
                    @save="saveForm('fair')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('series-annual-fair-abroad') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesFairAbroadForm
                        v-if="showForm('fair')"
                        ref="eventSeriesFairAbroadFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="fairAbroad.length"
                            :label="$t('fair-abroad')"
                            :items="fairAbroad"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('fair-abroad')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="fairAbroadCooperation.length"
                            :label="$t('fair-abroad-cooperation')"
                            :items="fairAbroadCooperation"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('fair-abroad-cooperation')"
                            text="-"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="competition" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('competition')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('competition')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('competition')"
                    @enter-edit-mode="onEnterEditMode('competition', $t('series-annual-competition'))"
                    @leave-edit-mode="leaveEditMode('competition')"
                    @discard-changes="discardChanges('competition')"
                    @save="saveForm('competition')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('series-annual-competition') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesCompetitionForm
                        v-if="showForm('competition')"
                        ref="eventSeriesCompetitionFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="competitionNational.length"
                            :label="$t('competition-national-label')"
                            :items="competitionNational"
                            :has-border-bottom="true"
                            :help-text="$t('competition-national-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('competition-national-label')"
                            text="-"
                            :has-border-bottom="true"
                            :help-text="$t('competition-national-help')"
                        />
                        <CardRowAttachmentGroup
                            v-if="otherCompetitionNational.length"
                            :label="$t('other-competition-national')"
                            :items="otherCompetitionNational"
                            :has-border-bottom="true"
                            :help-text="$t('other-competition-national-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('other-competition-national')"
                            text="-"
                            :has-border-bottom="true"
                            :help-text="$t('other-competition-national-help')"
                        />
                        <CardRowAttachmentGroup
                            v-if="competitionInternational.length"
                            :label="$t('competition-international-label')"
                            :items="competitionInternational"
                            :has-border-bottom="true"
                            :help-text="$t('competition-international-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('competition-international-label')"
                            text="-"
                            :has-border-bottom="true"
                            :help-text="$t('competition-international-help')"
                        />
                        <CardRowAttachmentGroup
                            v-if="otherCompetitionInternational.length"
                            :label="$t('other-competition-international')"
                            :items="otherCompetitionInternational"
                            :has-border-bottom="true"
                            :help-text="$t('other-competition-international-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('other-competition-international')"
                            text="-"
                            :help-text="$t('other-competition-international-help')"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="parallels" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('parallels')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('parallels')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('parallels')"
                    @enter-edit-mode="onEnterEditMode('parallels', $t('series-annual-favored-parallels'))"
                    @leave-edit-mode="leaveEditMode('parallels')"
                    @discard-changes="discardChanges('parallels')"
                    @save="saveForm('parallels')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('series-annual-favored-parallels') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesParallelsForm
                        v-if="showForm('parallels')"
                        ref="seriesParallelsFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="favoredParallelsInternal.length"
                            :label="$t('favored-parallels-internal')"
                            :items="favoredParallelsInternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('favored-parallels-internal')"
                            text="-"
                            :has-border-bottom="true"
                        />
                        <CardRowAttachmentGroup
                            v-if="favoredParallelsExternal.length"
                            :label="$t('favored-parallels-external')"
                            :items="favoredParallelsExternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('favored-parallels-external')"
                            text="-"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="otherLinks" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('otherLinks')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('otherLinks')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('otherLinks')"
                    @enter-edit-mode="onEnterEditMode('otherLinks', $t('series-annual-other-links'))"
                    @leave-edit-mode="leaveEditMode('otherLinks')"
                    @discard-changes="discardChanges('otherLinks')"
                    @save="saveForm('otherLinks')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('series-annual-other-links') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesOtherLinksForm
                        v-if="showForm('otherLinks')"
                        ref="eventSeriesOtherLinksFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="otherLinksInternal.length"
                            :label="$t('other-links-internal')"
                            :items="otherLinksInternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('other-links-internal')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="otherLinksExternal.length"
                            :label="$t('other-links-external')"
                            :items="otherLinksExternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('other-links-external')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowAttachmentGroup from '@/components/Card/CardRowAttachmentGroup.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import { toCardRowAttachmentLink } from '@/components/MultiLink/multilink';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';

import EventSeriesCompetitionForm from '@/event/forms/series/EventSeriesCompetitionForm.vue';
import EventSeriesDataForm from '@/event/forms/series/EventSeriesDataForm.vue';
import EventSeriesDocumentsForm from '@/event/forms/series/EventSeriesDocumentsForm.vue';
import EventSeriesFairAbroadForm from '@/event/forms/series/EventSeriesFairAbroadForm.vue';
import EventSeriesOtherLinksForm from '@/event/forms/series/EventSeriesOtherLinksForm.vue';
import EventSeriesParallelsForm from '@/event/forms/series/EventSeriesParallelsForm.vue';
import EventSeriesSubEventsForm from '@/event/forms/series/EventSeriesSubEventsForm.vue';
import EventSeriesSystemsForm from '@/event/forms/series/EventSeriesSystemsForm.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { useFluent } from 'fluent-vue';
import { computed, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const props = defineProps<{
    seriesId: string;
}>();

const fluent = useFluent();
const seriesId = computed(() => props.seriesId);
const { series } = useEventSeriesInjection();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    getCardStatus,
} = useActiveCard();

const extraDocuments = computed(() => toCardRowAttachmentLink(series.value?.extraDocuments?.links?.nodes));
const extraSystems = computed(() => toCardRowAttachmentLink(series.value?.extraSystems?.links?.nodes));
const internalData = computed(() => toCardRowAttachmentLink(series.value?.internalData?.links?.nodes));
const externalData = computed(() => toCardRowAttachmentLink(series.value?.externalData?.links?.nodes));
const subeventsLocal = computed(() => toCardRowAttachmentLink(series.value?.subeventsLocal?.links?.nodes));
const subeventsOtherLocations = computed(() =>
    toCardRowAttachmentLink(series.value?.subeventsOtherLocations?.links?.nodes),
);
const fairAbroad = computed(() => toCardRowAttachmentLink(series.value?.fairAbroad?.links?.nodes));
const fairAbroadCooperation = computed(() =>
    toCardRowAttachmentLink(series.value?.fairAbroadCooperation?.links?.nodes),
);
const competitionNational = computed(() => toCardRowAttachmentLink(series.value?.competitionNational?.links?.nodes));
const otherCompetitionNational = computed(() =>
    toCardRowAttachmentLink(series.value?.otherCompetitionNational?.links?.nodes),
);
const competitionInternational = computed(() =>
    toCardRowAttachmentLink(series.value?.competitionInternational?.links?.nodes),
);
const otherCompetitionInternational = computed(() =>
    toCardRowAttachmentLink(series.value?.otherCompetitionInternational?.links?.nodes),
);
const favoredParallelsInternal = computed(() =>
    toCardRowAttachmentLink(series.value?.favoredParallelsInternal?.links?.nodes),
);
const favoredParallelsExternal = computed(() =>
    toCardRowAttachmentLink(series.value?.favoredParallelsExternal?.links?.nodes),
);
const otherLinksInternal = computed(() => toCardRowAttachmentLink(series.value?.otherLinksInternal?.links?.nodes));
const otherLinksExternal = computed(() => toCardRowAttachmentLink(series.value?.otherLinksExternal?.links?.nodes));

const eventSeriesDocumentsFormRef = useTemplateRef('eventSeriesDocumentsFormRef');
const eventSeriesSystemsFormRef = useTemplateRef('eventSeriesSystemsFormRef');
const eventSeriesDataFormRef = useTemplateRef('eventSeriesDataFormRef');
const eventSeriesSubEventsForm = useTemplateRef('eventSeriesSubEventsForm');
const eventSeriesFairAbroadFormRef = useTemplateRef('eventSeriesFairAbroadFormRef');
const eventSeriesCompetitionFormRef = useTemplateRef('eventSeriesCompetitionFormRef');
const seriesParallelsFormRef = useTemplateRef('seriesParallelsFormRef');
const eventSeriesOtherLinksFormRef = useTemplateRef('eventSeriesOtherLinksFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('documents', eventSeriesDocumentsFormRef);
register('systems', eventSeriesSystemsFormRef);
register('data', eventSeriesDataFormRef);
register('subevents', eventSeriesSubEventsForm);
register('fair', eventSeriesFairAbroadFormRef);
register('competition', eventSeriesCompetitionFormRef);
register('parallels', seriesParallelsFormRef);
register('otherLinks', eventSeriesOtherLinksFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
</script>
